import { motion } from "framer-motion";

const IMG =
  "https://images.unsplash.com/photo-1544984243-ec57ea16fe25?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3VzZSUyMGFyY2hpdGVjdHVyZSUyMHN1bnNldHxlbnwwfHx8fDE3OTAzNDYwNzh8MA&ixlib=rb-4.1.0&q=85";

const STATS = [
  { metric: "19 vs 12", unit: "days", label: "Make-Ready Velocity Gap", insight: "Benchmark your turn times against comparable operators before owner asset reviews expose them." },
  { metric: "98.4%", unit: "", label: "Audit Compliance Benchmark", insight: "Top-decile operators run weekly walks, timestamped violations, and certified staff on OPERA360 standards." },
  { metric: "-41%", unit: "", label: "Manager First-Year Churn", insight: "Structured 30/60/90 onboarding keeps community managers past the industry danger zone." },
];

export const Intelligence = () => (
  <section id="intelligence" className="relative py-24 sm:py-32 border-t border-slate-200" data-testid="intelligence-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-12 gap-12 items-center mb-14">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7"
        >
          <p className="eyebrow mb-4 flex items-center gap-3">
            <span className="inline-block h-px w-10 bg-cadmium" /> Operational Intelligence
          </p>
          <h2 className="font-cabinet text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">
            Your data becomes a <span className="font-editorial italic font-normal text-sky-600">benchmarking engine.</span>
          </h2>
          <p className="mt-5 max-w-xl text-base text-muted-foreground leading-relaxed">
            Once your communities generate operational data, OPERA360 tells you things no accounting system can —
            where you lead, where you lag, and which communities show early indicators of occupancy deterioration.
          </p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5"
        >
          <div className="relative overflow-hidden rounded-md border border-slate-200" style={{ clipPath: "polygon(0 0, 100% 0, 100% 88%, 92% 100%, 0 100%)" }}>
            <img src={IMG} alt="Sunset over a modern multifamily property" className="h-52 sm:h-64 w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#EDF4FB]/90 to-transparent" />
            <span className="absolute bottom-3 left-4 text-[10px] font-mono uppercase tracking-widest text-slate-900/80">
              Asset care, measured
            </span>
          </div>
        </motion.div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {STATS.map((s, i) => (
          <motion.div
            key={s.label}
            data-testid={`intelligence-stat-${i}`}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="technical-card p-7"
          >
            <p className="font-mono text-4xl sm:text-5xl font-semibold text-slate-900 tracking-tight">
              {s.metric}
              {s.unit && <span className="ml-2 text-base text-muted-foreground">{s.unit}</span>}
            </p>
            <p className="mt-3 text-xs font-mono uppercase tracking-[0.2em] text-sky-600">{s.label}</p>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.insight}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
